import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { sv } from '@aldorock-fordon/shared/sv';
import type { PoolSummary, PoolVehicleItem } from '@aldorock-fordon/shared/api-types';
import { api, type Me } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { Facts } from '../ui/Facts.tsx';
import { Table } from '../ui/Table.tsx';
import { plate } from '../format.ts';

function PoolVehicles({ pool }: { pool: PoolSummary }) {
  const q = useQuery({
    queryKey: ['pool-vehicles', pool.id],
    queryFn: () => api<PoolVehicleItem[]>(`/pools/${pool.id}/vehicles`),
  });
  if (!q.data) return <p role="status">{sv.app.loading}</p>;
  return (
    <Table caption={sv.pools.vehiclesCaption(pool.name)} hasOptional>
      <thead>
        <tr>
          <th scope="col" className="r">
            {sv.columns.priority}
          </th>
          <th scope="col">{sv.columns.vehicle}</th>
          <th scope="col" className="col-opt">
            {sv.columns.fuel}
          </th>
          <th scope="col" className="r col-opt">
            {sv.columns.seats}
          </th>
          <th scope="col" className="col-opt">
            {sv.columns.slot}
          </th>
          <th scope="col">{sv.columns.note}</th>
        </tr>
      </thead>
      <tbody>
        {q.data.map((v) => (
          <tr key={v.vehicleId}>
            <td className="r">{v.priority}</td>
            <td>
              <Link to={`/fordon/${v.vehicleId}`}>
                {[v.make, v.model].filter(Boolean).join(' ')} {plate(v.registrationNumber)}
              </Link>
            </td>
            <td className="col-opt">{v.fuel ? sv.fuels[v.fuel] : ''}</td>
            <td className="r col-opt">{v.seats ?? ''}</td>
            <td className="col-opt">{v.slotNumber ?? ''}</td>
            <td>{v.note ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function PoolsPage({ me }: { me: Me }) {
  const h1 = usePage(sv.pools.title);
  const q = useQuery({ queryKey: ['pools'], queryFn: () => api<PoolSummary[]>('/pools') });
  return (
    <>
      <PageHead help="pools" title={sv.pools.title} titleRef={h1} />
      <p>{sv.pools.intro}</p>
      {q.isPending ? <p role="status">{sv.app.loading}</p> : null}
      {q.data?.map((p) => (
        <section className="block" key={p.id} aria-labelledby={`pool-${p.id}`}>
          <h2 className="block-title" id={`pool-${p.id}`}>
            {p.name}
            {p.isDefault ? (
              <span className="muted"> ({sv.pools.default.toLowerCase()})</span>
            ) : null}
          </h2>
          {p.description ? <p>{p.description}</p> : null}
          {me.permissions['pool.manage'] ? (
            <p className="actions-inline">
              <Link className="btn" to={`/bilpooler/${p.id}`}>
                {sv.poolAdmin.edit}
              </Link>
            </p>
          ) : null}
          <Facts
            top={false}
            facts={[
              { key: 'g', label: sv.pools.garage, value: p.garage ?? sv.none },
              { key: 'a', label: sv.pools.address, value: p.address ?? sv.none },
              {
                key: 'k',
                label: sv.pools.cabinet,
                value: p.cabinet ? p.cabinet.name : sv.pools.keyMode[p.keyMode],
              },
              { key: 'p', label: sv.pools.price, value: sv.pools.priceLine(p.priceModel) },
              { key: 'n', label: sv.columns.count, value: sv.pools.vehicles(p.vehicleCount) },
              {
                key: 'm',
                label: sv.pools.members,
                value: p.isDefault
                  ? sv.pools.membersAll
                  : p.member
                    ? sv.pools.member
                    : sv.pools.notMember,
              },
              p.adminNote ? { key: 'note', label: sv.pools.adminNote, value: p.adminNote } : null,
            ]}
          />
          <PoolVehicles pool={p} />
        </section>
      ))}
    </>
  );
}
